/**
 * League-wide all-time records: the leaderboards behind the Hall of Fame and
 * Shame. Every list is short, best first, and ties go to the most recent: a
 * mark matched later ranks ahead of the older one. Consolation games are left
 * out, as everywhere else in the history.
 */

import type { LeagueHistory } from "../history";
import { getAwardDefinition } from "../types";
import { round2 } from "../util/math";
import type { PersonGame } from "../reportBuilder/ledger";
import { buildLedger, countsTowardHistory } from "../reportBuilder/ledger";
import type { GameRecord } from "./gameRecords";
import { toGameRecord, topBy } from "./gameRecords";
import type { ManagerSeasonLine } from "./seasonLines";
import { buildSeasonLines, compareSeasonLines } from "./seasonLines";
import type { Streak } from "./streaks";
import { compareStreaks, summarizeStreaks } from "./streaks";

/** Entries per leaderboard. */
const LEADERBOARD_SIZE = 5;

export interface StreakRecord extends Streak {
  personId: string;
}

/** One manager's total on a career board. */
export interface CareerLeader {
  personId: string;
  count: number;
  /** The distinct seasons that added to the count, oldest first. */
  seasons: number[];
}

export interface HallOfFame {
  /** Holder is the scorer. */
  highestScores: GameRecord[];
  /** Holder is the low scorer. */
  lowestScores: GameRecord[];
  /** Holder is the winner. */
  biggestBlowouts: GameRecord[];
  /** Holder is the winner (the first-listed side of a tie). */
  closestGames: GameRecord[];
  /** Both teams' points added; holder is the winner (first-listed side of a tie). */
  highestScoringGames: GameRecord[];
  /** Each manager's own best run, longest first. */
  longestWinStreaks: StreakRecord[];
  longestLossStreaks: StreakRecord[];
  /** Most titles first; managers with none are omitted. */
  titles: { personId: string; seasons: number[] }[];
  /** Finished seasons only. */
  bestSeasons: ManagerSeasonLine[];
  worstSeasons: ManagerSeasonLine[];
  /** All-time totals, most first; managers with none are omitted. */
  careers: {
    /** Playoff games count; consolation doesn't. */
    mostWins: CareerLeader[];
    /** Finished seasons in which they played a playoff game. */
    mostPlayoffAppearances: CareerLeader[];
    /** Finished seasons that ended second. */
    mostRunnerUps: CareerLeader[];
    /** Regular-season weeks with the league's top score. */
    mostWeeklyHighs: CareerLeader[];
    /** Weeks the manager had the Biggest Blowout award: the week's largest margin of victory. */
    mostBlowoutAwards: CareerLeader[];
  };
}

export function buildHallOfFame(history: LeagueHistory): HallOfFame {
  const ledger = buildLedger(history);
  const games = ledger.filter(countsTowardHistory);
  const finishedSeasons = buildSeasonLines(history, ledger).filter((l) => l.complete);

  // Each game once, from the winner's side (a tie keeps its alphabetically first side).
  const decided = games.filter((g) => g.result === "W" || (g.result === "T" && g.personId < g.opponentId));
  const margin = (g: PersonGame) => Math.abs(g.points - g.opponentPoints);
  // topBy's sort is stable, so feeding it newest-first puts the latest of equal scores on top.
  const records = (list: readonly PersonGame[], score: (g: PersonGame) => number) =>
    topBy([...list].reverse(), score, LEADERBOARD_SIZE).map(toGameRecord);

  const streaksByPerson = history.people.map((person) => ({
    personId: person.id,
    ...summarizeStreaks(games.filter((g) => g.personId === person.id)),
  }));
  const streakBoard = (pick: (s: (typeof streaksByPerson)[number]) => Streak | null): StreakRecord[] =>
    streaksByPerson
      .flatMap((s) => {
        const streak = pick(s);
        return streak ? [{ personId: s.personId, ...streak }] : [];
      })
      .sort(compareStreaks)
      .slice(0, LEADERBOARD_SIZE);

  return {
    highestScores: records(games, (g) => g.points),
    lowestScores: records(games, (g) => -g.points),
    biggestBlowouts: records(decided, margin),
    closestGames: records(decided, (g) => -margin(g)),
    highestScoringGames: records(decided, (g) => g.points + g.opponentPoints),
    longestWinStreaks: streakBoard((s) => s.longestWin),
    longestLossStreaks: streakBoard((s) => s.longestLoss),
    titles: titlesOf(finishedSeasons),
    bestSeasons: [...finishedSeasons].sort((a, b) => compareSeasonLines(a, b) || b.season - a.season).slice(0, LEADERBOARD_SIZE),
    worstSeasons: [...finishedSeasons].sort((a, b) => compareSeasonLines(b, a) || b.season - a.season).slice(0, LEADERBOARD_SIZE),
    careers: {
      mostWins: careerBoard(games.filter((g) => g.result === "W")),
      mostPlayoffAppearances: careerBoard(finishedSeasons.filter((l) => l.madePlayoffs)),
      mostRunnerUps: careerBoard(finishedSeasons.filter((l) => l.finish === 2)),
      mostWeeklyHighs: careerBoard(weeklyHighs(games.filter((g) => g.gameType === "regular"))),
      mostBlowoutAwards: careerBoard(weeklyBlowouts(games, incompleteWeeks(history))),
    },
  };
}

/** One count per hit, ranked by total; equal totals go to whoever's latest hit is most recent. */
function careerBoard(hits: readonly { personId: string; season: number }[]): CareerLeader[] {
  const tally = new Map<string, { count: number; seasons: Set<number> }>();
  for (const { personId, season } of hits) {
    const entry = tally.get(personId) ?? { count: 0, seasons: new Set<number>() };
    entry.count += 1;
    entry.seasons.add(season);
    tally.set(personId, entry);
  }
  return [...tally]
    .map(([personId, { count, seasons }]) => ({ personId, count, seasons: [...seasons].sort((a, b) => a - b) }))
    .sort((a, b) => b.count - a.count || b.seasons.at(-1)! - a.seasons.at(-1)!)
    .slice(0, LEADERBOARD_SIZE);
}

/** "season-week" for every week of a live season that still has games to play. */
export function incompleteWeeks(history: LeagueHistory): Set<string> {
  return new Set(history.seasons.flatMap((s) => s.upcoming.map((u) => `${s.season}-${u.week}`)));
}

/**
 * The site's Biggest Blowout award, replayed over every season: in each regular-season week up to the
 * award's last week, the winner of the single largest margin (equal margins go to the higher score).
 * A week that isn't fully played yet has no winner, as on the Blowout page.
 */
export function weeklyBlowouts(games: readonly PersonGame[], incomplete: ReadonlySet<string>): PersonGame[] {
  const { firstWeek, lastWeek } = getAwardDefinition("biggestBlowout");
  const best = new Map<string, PersonGame>();
  const marginOf = (g: PersonGame) => round2(g.points - g.opponentPoints);
  for (const g of games) {
    const key = `${g.season}-${g.week}`;
    if (g.gameType !== "regular" || g.week < firstWeek || g.week > lastWeek || incomplete.has(key)) continue;
    if (g.result !== "W") continue; // a tie has no winner, so it can't be a blowout
    const current = best.get(key);
    if (!current || marginOf(g) > marginOf(current) || (marginOf(g) === marginOf(current) && g.points > current.points)) best.set(key, g);
  }
  return [...best.values()];
}

/** Every game that was its week's top score, so a tie for the top credits both teams. */
function weeklyHighs(regularGames: readonly PersonGame[]): PersonGame[] {
  const best = new Map<string, number>();
  const weekKey = (g: PersonGame) => `${g.season}-${g.week}`;
  for (const g of regularGames) best.set(weekKey(g), Math.max(best.get(weekKey(g)) ?? -Infinity, g.points));
  return regularGames.filter((g) => g.points === best.get(weekKey(g)));
}

function titlesOf(finishedSeasons: readonly ManagerSeasonLine[]): HallOfFame["titles"] {
  const byPerson = new Map<string, number[]>();
  for (const line of finishedSeasons.filter((l) => l.champion)) {
    byPerson.set(line.personId, [...(byPerson.get(line.personId) ?? []), line.season]);
  }
  return [...byPerson.entries()]
    .map(([personId, seasons]) => ({ personId, seasons }))
    // Equal counts: whoever won most recently. `seasons` is oldest first, so its last entry is the latest title.
    .sort((a, b) => b.seasons.length - a.seasons.length || b.seasons.at(-1)! - a.seasons.at(-1)!);
}
